import {
  Temporal,
  toPlainDate,
  formatShortDate,
  formatLongDate,
  formatDateWithDay,
  formatDateRange as formatDateRangeLib,
  formatTimestamp as formatTimestampLib,
  formatDotTimestamp,
  formatDueDate as formatDueDateLib,
  formatRelativeDate as formatRelativeDateLib,
  formatRelativeInstant
} from '@insio/dates';
import { useLocale, useTimeZone } from '../providers/UIProvider';

// ─── Date formatter hooks ─────────────────────────────────────────────────────
//
// All hooks read `dateLocale` and `timeZone` from the nearest `UIProvider`
// and return a bound formatter function that accepts ISO date/datetime strings
// (as received from GraphQL) and returns a locale+timezone-aware display string.
//
// The underlying pure functions live in `@insio/dates` — these hooks are the
// React ergonomic layer on top.
//
// IMPORTANT: When the input is a full ISO timestamp (contains 'T'), date-only
// formatters first convert to the user's time zone before extracting the
// calendar date, preventing off-by-one errors near midnight UTC.
//

/** Returns a formatter: PlainDate / Instant → short date (e.g. "19. 11. 2025" / "11/19/2025") */
export function useFormatShortDate(): (date: Temporal.PlainDate | Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => {
    return formatShortDate(toPlainDate(date, timeZone), locale);
  };
}

/** Returns a formatter: PlainDate / Instant → long date (e.g. "19. listopadu 2025" / "November 19, 2025") */
export function useFormatLongDate(): (date: Temporal.PlainDate | Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => formatLongDate(toPlainDate(date, timeZone), locale);
}

/** Returns a formatter: PlainDate / Instant → date with day of week (e.g. "úterý 19. listopadu 2025") */
export function useFormatDateWithDay(): (date: Temporal.PlainDate | Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => formatDateWithDay(toPlainDate(date, timeZone), locale);
}

/** Returns a formatter: Instant → timestamp with time zone (e.g. "19. 11. 2025 14:30") */
export function useFormatTimestamp(): (date: Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => formatTimestampLib(date, locale, timeZone);
}

/**
 * Returns a formatter: Instant → fixed dot-format timestamp ("19.11.2025 14:30").
 * Unlike useFormatTimestamp this is locale-agnostic — always dd.MM.yyyy HH:mm.
 * Use this where a compact, consistent format is required (e.g. audit-trail modals).
 */
export function useFormatDotTimestamp(): (date: Temporal.Instant) => string {
  const timeZone = useTimeZone();
  return date => formatDotTimestamp(date, timeZone);
}

/** Returns a formatter: Instant / PlainDate → relative time (e.g. "před 2 dny" / "2 days ago") */
export function useFormatRelativeDate(): (date: Temporal.PlainDate | Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => {
    if (date instanceof Temporal.Instant) {
      return formatRelativeInstant(date, locale);
    }
    return formatRelativeDateLib(toPlainDate(date, timeZone), locale);
  };
}

/** Returns a formatter: PlainDate / Instant → due date label (e.g. "dnes" / "today", "za 5 dní" / "in 5 days") */
export function useFormatDueDate(): (date: Temporal.PlainDate | Temporal.Instant) => string {
  const locale = useLocale();
  const timeZone = useTimeZone();
  return date => formatDueDateLib(toPlainDate(date, timeZone), locale);
}

/** Returns a formatter: (from, to) PlainDates → date range (e.g. "1. 11. 2025 – 30. 11. 2025") */
export function useFormatDateRange(): (from: Temporal.PlainDate, to: Temporal.PlainDate) => string {
  const locale = useLocale();
  return (from, to) => formatDateRangeLib(from, to, locale);
}
